import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService, LC_KEY_PRODUCT_FULL, LC_KEY_PRODUCT_FULL_HISTORY_LIST, LC_PRODUCT_FULL_STORAGE_TIME, LocalStorage } from 'src/toolkit';
import { TTableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { TProductFullList, TProductFullListResponse, TProductFullListQuery, TProductFullListSortCol, TProductFullPayloadRequest, adaptProductFullToRequest, adaptProductFullFromListResponse, TProductFullHistoryItem } from '../../types';
import { BACKEND_PRODUCT_FULL } from '../../backend-paths';

export const productFullListGetApi = createAsyncThunk(
	BACKEND_PRODUCT_FULL,
	(payload: TProductFullPayloadRequest, thunkApi) => {
		const params = adaptProductFullToRequest(payload);

		const key = `${LC_KEY_PRODUCT_FULL}/${JSON.stringify(params)}`;
		const value = LocalStorage.get(key);

		if (value) {
			const { updatedTimestampAt, result } = value;
			const diff = Date.now() - updatedTimestampAt;

			if (diff <= LC_PRODUCT_FULL_STORAGE_TIME) {
				console.log('FROM STORAGE');

				return result;
			}
		}

		console.log('FROM DATABASE');

		const config = {
			params,
			timeout: 60000,
		};

		return HttpService.get<TProductFullListResponse>(BACKEND_PRODUCT_FULL, config).then(response => {
			const { data } = response;

			return adaptProductFullFromListResponse(data).then(result => {
				// LocalStorage.set(key, { updatedTimestampAt: Date.now(), result });

				const historyItemList: TProductFullHistoryItem[] = LocalStorage.get(LC_KEY_PRODUCT_FULL_HISTORY_LIST) || [];

				const date = new Date();
				const itemKey = JSON.stringify(payload);

				const historyItem: TProductFullHistoryItem = {
					...payload,
					date: date.toISOString(),
					key: itemKey,
				};

				const filtered = historyItemList.filter(item => item.key !== itemKey);

				filtered.unshift(historyItem);

				LocalStorage.set(LC_KEY_PRODUCT_FULL_HISTORY_LIST, filtered);

				return result;
			}).catch((error: HttpError) => {
				return thunkApi.rejectWithValue({ ...error });
			});
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TProductFullList;
	showList: TProductFullList;
	query: TProductFullListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	showList: {
		items: [],
		count: 0,
	},
	query: {
		limit: 50,
		offset: 0,
		search: undefined,
		searchBrandName: undefined,
		searchSerialNumber: undefined,
		searchProvider: undefined,
		searchName: undefined,
		searchStorageName: undefined,
		brandId: undefined,
		userNickname: undefined,
		isAnalogues: false,
		serialNumber: '',
	},
	error: undefined,
};

export const productFullListSlice = createSlice({
	name: 'productFullListSlice',
	initialState,
	reducers: {
		setProductFullListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setProductFullListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setProductFullListQueryShowList(state, action: PayloadAction<TProductFullList>) {
			state.showList = action.payload;
		},

		setProductFullListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setProductFullListQuerySearchBrandName(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchBrandName = value;
		},

		setProductFullListQuerySearchSerialNumber(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchSerialNumber = value;
		},

		setProductFullListQuerySearchProvider(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchProvider = value;
		},

		setProductFullListQuerySearchName(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchName = value;
		},

		setProductFullListQuerySearchStorageName(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchStorageName = value;
		},

		setProductFullListQueryBrand(state, action: PayloadAction<number | undefined>) {
			const brandId = action.payload || undefined;
			const isBrandId = !!brandId && brandId > 0;

			state.query.brandId = isBrandId ? brandId : undefined;
		},

		setProductFullListQueryUserNickname(state, action: PayloadAction<string>) {
			const userNickname = action.payload || undefined;
			const isUserNickname = userNickname && userNickname.length > 0;

			state.query.userNickname = isUserNickname ? userNickname : '';
		},

		setProductFullListQueryIsAnalogues(state, action: PayloadAction<boolean>) {
			state.query.isAnalogues = action.payload;
		},

		setProductFullListQuerySerialNumber(state, action: PayloadAction<string>) {
			const serialNumber = action.payload || undefined;

			state.query.serialNumber = serialNumber ? serialNumber : '';
		},

		setProductFullListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setProductFullListQuerySortCol(state, action: PayloadAction<TProductFullListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearProductFullList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[productFullListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[productFullListGetApi.fulfilled.type]: (state, action: PayloadAction<TProductFullList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[productFullListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useProductFullListSelector = () => useAppSelector(state => state[productFullListSlice.name]);

export const { clearProductFullList, setProductFullListQuerySearch, setProductFullListQuerySearchBrandName, setProductFullListQuerySearchSerialNumber, setProductFullListQuerySearchName, setProductFullListQuerySearchStorageName, setProductFullListQuerySearchProvider, setProductFullListQuerySerialNumber, setProductFullListQueryBrand, setProductFullListQueryUserNickname, setProductFullListQueryIsAnalogues, setProductFullListQuerySort, setProductFullListQuerySortCol, setProductFullListQueryLimit, setProductFullListQueryOffset, setProductFullListQueryShowList } = productFullListSlice.actions;
